/**
 * 직원 관리 목록의 필터·정렬을 브라우저에 기억한다 (CS-OKBEST-260923-0001).
 *
 * 새로고침·다른 브라우저 탭에서 돌아와도 걸어 둔 필터가 그대로 있어야 한다는 요청.
 * 탭(LNB) 이동은 컴포넌트가 살아 있어 원래 유지되고, 잃는 건 페이지 리로드뿐이라
 * localStorage 에 둔다. 키에는 조직 id 와 사용자 id 를 넣는다 — 한 브라우저에서
 * 워크스페이스를 오가거나 계정을 바꿔도 남의 팀 id 를 복원하지 않게.
 *
 * 저장 시점은 `loadEmployees()` 하나다 — 필터를 바꾸는 모든 경로(드롭다운·검색·
 * 초기화·근무 설정에서 넘어오기)가 거기로 모이므로 따로 훅을 두지 않는다.
 * 페이지 번호는 저장하지 않는다(필터가 바뀌면 어차피 1페이지로 돌아간다).
 *
 * 읽을 때는 모양을 검사한다 — 예전 버전이 남긴 값이나 손으로 고친 값이 들어와도
 * 문자열 배열·허용된 정렬 키가 아니면 그 항목만 기본값으로 둔다.
 */
import { lastSelectionKey } from "@hereby/components";

export interface EmployeeFilterState {
  search: string;
  statuses: string[];
  teamIds: string[];
  roleIds: string[];
  shiftGroupIds: string[];
  shiftIds: string[];
  hireDateFrom: string;
  hireDateTo: string;
  jobDuties: string[];
  wardIds: string[];
  /** 앱 온보딩 단계(「앱 상태」) — 값은 AppOnboardingStage */
  appStages: string[];
}

export type EmployeeSortBy =
  | ""
  | "employeeNumber"
  | "lastName"
  | "email"
  | "hireDate"
  | "status"
  | "jobDuty";

export interface EmployeeSortState {
  by: EmployeeSortBy;
  order: "asc" | "desc";
}

export interface EmployeeListState {
  filter: EmployeeFilterState;
  sort: EmployeeSortState;
}

const SORT_KEYS: ReadonlySet<string> = new Set([
  "",
  "employeeNumber",
  "lastName",
  "email",
  "hireDate",
  "status",
  "jobDuty",
]);

const LIST_KEYS = [
  "statuses",
  "teamIds",
  "roleIds",
  "shiftGroupIds",
  "shiftIds",
  "jobDuties",
  "wardIds",
  "appStages",
] as const;
const TEXT_KEYS = ["search", "hireDateFrom", "hireDateTo"] as const;

export function emptyEmployeeFilter(): EmployeeFilterState {
  return {
    search: "",
    statuses: [],
    teamIds: [],
    roleIds: [],
    shiftGroupIds: [],
    shiftIds: [],
    hireDateFrom: "",
    hireDateTo: "",
    jobDuties: [],
    wardIds: [],
    appStages: [],
  };
}

export function employeeListStateKey(
  organizationId?: string | null,
  userId?: string | null,
): string {
  return `${lastSelectionKey("admin", "employeeList", organizationId)}:${userId || "me"}`;
}

const stringList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

/** 저장된 JSON(또는 아무 값)을 검증해 목록 상태로 만든다. 모양이 아니면 null. */
export function parseEmployeeListState(raw: unknown): EmployeeListState | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const src = raw as { filter?: unknown; sort?: unknown };
  const f = (src.filter && typeof src.filter === "object" ? src.filter : {}) as Record<
    string,
    unknown
  >;
  const filter = emptyEmployeeFilter();
  for (const key of LIST_KEYS) {
    filter[key] = stringList(f[key]);
  }
  for (const key of TEXT_KEYS) {
    filter[key] = typeof f[key] === "string" ? (f[key] as string) : "";
  }
  const s = (src.sort && typeof src.sort === "object" ? src.sort : {}) as Record<string, unknown>;
  const by = typeof s.by === "string" && SORT_KEYS.has(s.by) ? (s.by as EmployeeSortBy) : "";
  const order = s.order === "desc" ? "desc" : "asc";
  return { filter, sort: { by, order } };
}

export function readEmployeeListState(key: string): EmployeeListState | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? parseEmployeeListState(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function writeEmployeeListState(key: string, state: EmployeeListState): void {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // 사생활 모드·용량 초과 — 기억만 포기하고 목록은 그대로 돈다.
  }
}
